import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../../components/layout/AppLayout';
import { useCourseApi } from '../../../../api/courses/courses';
import { TLesson } from '../../../../models/courses/lesson';
import { TH5PLessonConfig } from '../../../../models/courses/h5p';
import H5PLessonAdmin from '../../../../components/H5PLesson/admin/H5PLessonAdmin';

const defaultConfig: TH5PLessonConfig = {
  videoUrl: '',
  allowSkipping: false,
  quizzes: [],
};

const LessonH5P: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getLessonDetailsById, saveLessonDetails } = useCourseApi();
  const { t } = useTranslation();

  const stateLesson: TLesson | undefined = location.state?.lesson;
  const epic = location.state?.epic;

  const [editingLesson, setEditingLesson] = useState<TLesson | null>(stateLesson ?? null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importedStatement, setImportedStatement] = useState<string>('');

  const lessonIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const lessonId = params.get('lessonId');
    return lessonId ? parseInt(lessonId, 10) : undefined;
  }, [location.search]);

  useEffect(() => {
    const lessonId = lessonIdFromUrl ?? stateLesson?.id;
    if (!lessonId) {
      message.warning(t('h5p.page.messages.selectLesson'));
      navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      return;
    }
    setLoading(true);
    getLessonDetailsById(lessonId)
      .then((response) => {
        const lessonData = response.data;
        if (!lessonData.config) {
          lessonData.config = { id: 0, lesson_id: lessonData.id, config: defaultConfig };
        } else {
          lessonData.config = {
            ...lessonData.config,
            config: {
              videoUrl: lessonData.config.config?.videoUrl ?? '',
              allowSkipping: lessonData.config.config?.allowSkipping ?? false,
              quizzes: lessonData.config.config?.quizzes ?? [],
            },
          };
        }
        setEditingLesson(lessonData);
        // Initialize imported statement from lesson
        setImportedStatement(lessonData.statement || '');
      })
      .catch((error) => {
        message.error(error.response?.data ?? error.message);
        navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      })
      .finally(() => setLoading(false));
  }, []);

  const currentConfig: TH5PLessonConfig = useMemo(() => {
    const rawConfig = editingLesson?.config?.config as TH5PLessonConfig | undefined;
    return {
      videoUrl: rawConfig?.videoUrl ?? '',
      allowSkipping: rawConfig?.allowSkipping ?? false,
      quizzes: rawConfig?.quizzes ?? [],
    };
  }, [editingLesson]);

  const handleConfigChange = (config: TH5PLessonConfig) => {
    setEditingLesson((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        config: {
          id: prev.config?.id ?? 0,
          lesson_id: prev.id,
          config,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!editingLesson) {
      return;
    }
    setSaving(true);
    try {
      const payload: TLesson = {
        ...editingLesson,
        // Prioritize imported statement over existing lesson statement
        statement: importedStatement || editingLesson.statement || '',
        config: {
          id: editingLesson.config?.id ?? 0,
          lesson_id: editingLesson.id,
          config: currentConfig,
        },
      };
      await saveLessonDetails(payload);
      message.success(t('h5p.page.messages.saveSuccess'));
    } catch (error: any) {
      message.error(error.response?.data ?? error.message ?? error);
    } finally {
      setSaving(false);
    }
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <AppLayout
      title={editingLesson?.name || t('h5p.page.title')}
      onBackNavigation={handleBackNavigation}
      showBackButton
      loading={loading}
    >
      {editingLesson && (
        <H5PLessonAdmin
          config={currentConfig}
          onConfigChange={handleConfigChange}
          onSave={handleSave}
          saving={saving}
          initialStatement={importedStatement}
          onStatementChange={setImportedStatement}
        />
      )}
    </AppLayout>
  );
};

export default LessonH5P;